import { useLayoutEffect, useState, type ReactNode, type RefObject } from "react";
import { Dialog as D } from "radix-ui";
import { focusFirstField, restoreFocus, useMountOpener } from "../../lib/focus";
import { X } from "lucide-react";
import { cn } from "../../lib/cn";
import { useKitCopy } from "../../lib/kit-copy";

export const DialogRoot = D.Root;
export const DialogTrigger = D.Trigger;
export const DialogClose = D.Close;

/**
 * The body is a keyboard stop only while it scrolls (axe scrollable-region-focusable): it is measured on its own
 * resize and on its content's (`inner`), which grows without resizing a body already at its full height.
 */
function useScrolls() {
  const [body, setBody] = useState<HTMLDivElement | null>(null);
  const [inner, setInner] = useState<HTMLDivElement | null>(null);
  const [scrolls, setScrolls] = useState(false);
  useLayoutEffect(() => {
    if (!body) return;
    const check = () => setScrolls(body.scrollHeight > body.clientHeight);
    check();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(check);
    ro.observe(body);
    if (inner) ro.observe(inner);
    return () => ro.disconnect();
  }, [body, inner]);
  return { setBody, setInner, scrolls };
}

/**
 * Modal dialog (Radix): focus trap, Escape closes. On open, focus goes to the
 * first field or the title (not the primary action); on close it returns to
 * the opener, also for controlled dialogs without a Trigger.
 * Never taller than the viewport: the title and the footer stay in view and the
 * body between them scrolls (a long form on a short laptop screen). A click on the
 * overlay around it closes it, like Escape.
 */
export function DialogContent({ title, description, children, footer, className, hideClose, returnFocusRef }: { title: ReactNode; description?: ReactNode; children?: ReactNode; footer?: ReactNode; className?: string; hideClose?: boolean; returnFocusRef?: RefObject<HTMLElement | null> }) {
  const opener = useMountOpener();
  const l = useKitCopy().labels;
  const { setBody, setInner, scrolls } = useScrolls();
  return (
    <D.Portal>
      {/* The content sits in the overlay: the overlay is the viewport minus its padding, so the dialog never outgrows it. */}
      <D.Overlay className="fixed inset-0 z-(--z-modal) flex items-center justify-center bg-scrim p-4">
        <D.Content
          onOpenAutoFocus={focusFirstField}
          onCloseAutoFocus={(e) => restoreFocus(returnFocusRef?.current ?? opener)(e)}
          className={cn("relative flex max-h-full w-full max-w-lg flex-col rounded-modal border border-border bg-surface shadow-overlay", className)}
        >
          {!hideClose && (
            <D.Close aria-label={l.close} className="absolute right-3 top-3 inline-grid size-touch place-items-center rounded-btn text-text-muted hover:bg-surface-2 lg:size-8">
              <X aria-hidden className="size-4" />
            </D.Close>
          )}
          <div data-dialog-header className={cn("shrink-0 space-y-1.5 pl-6 pt-6", hideClose ? "pr-6" : "pr-14", !children && !footer && "pb-6")}>
            <D.Title data-dialog-title tabIndex={-1} className="heading text-lg outline-none">
              {title}
            </D.Title>
            {description ? <D.Description className="text-sm text-text-muted">{description}</D.Description> : <D.Description className="sr-only">{title}</D.Description>}
          </div>
          {/* The only scroller; its padding keeps focus rings inside it. */}
          {children && (
            <div ref={setBody} data-dialog-body tabIndex={scrolls ? 0 : undefined} className={cn("mt-3 min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pt-1", footer ? "pb-1" : "pb-6")}>
              <div ref={setInner}>{children}</div>
            </div>
          )}
          {footer && (
            <div data-dialog-footer className={cn("flex shrink-0 flex-col-reverse gap-2 px-6 pb-6 sm:flex-row sm:justify-end", children ? "pt-5" : "pt-6")}>
              {footer}
            </div>
          )}
        </D.Content>
      </D.Overlay>
    </D.Portal>
  );
}

/** Side panel / drawer (05 §4), from the right on desktop and the bottom on phones. */
export function SheetContent({ title, description, children, footer, returnFocusRef }: { title: ReactNode; description?: ReactNode; children?: ReactNode; footer?: ReactNode; returnFocusRef?: RefObject<HTMLElement | null> }) {
  const opener = useMountOpener();
  const l = useKitCopy().labels;
  return (
    <D.Portal>
      <D.Overlay className="fixed inset-0 z-(--z-overlay) bg-scrim" />
      <D.Content
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement).querySelector<HTMLElement>("[data-dialog-title]")?.focus();
        }}
        onCloseAutoFocus={(e) => restoreFocus(returnFocusRef?.current ?? opener)(e)}
        className="fixed inset-x-0 bottom-0 top-24 z-(--z-overlay) flex flex-col rounded-t-modal border border-border bg-surface shadow-overlay lg:inset-y-0 lg:left-auto lg:right-0 lg:top-0 lg:w-aside-wide lg:rounded-none lg:rounded-l-modal">
        <div className="flex items-start gap-3 border-b border-border p-5">
          <div className="min-w-0 flex-1 space-y-1">
            <D.Title data-dialog-title tabIndex={-1} className="heading text-lg outline-none">
              {title}
            </D.Title>
            {description ? <D.Description className="text-sm text-text-muted">{description}</D.Description> : <D.Description className="sr-only">{title}</D.Description>}
          </div>
          <D.Close aria-label={l.closePanel} className="inline-grid size-touch place-items-center rounded-btn text-text-muted hover:bg-surface-2 lg:size-8">
            <X aria-hidden className="size-4" />
          </D.Close>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
        {footer && <div className="flex gap-2 border-t border-border p-4">{footer}</div>}
      </D.Content>
    </D.Portal>
  );
}
